<template>
  <div class="container mt-4 mb-5">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h3 class="m-0">Artikelverwaltung</h3>
      <router-link class="btn btn-outline-secondary btn-sm" :to="{ name: 'Invoices' }">
        Zurück zur Rechnungsübersicht
      </router-link>
    </div>

    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-2 align-items-end">
          <div class="col-md-7">
            <label class="form-label" for="articleSearch">Suche</label>
            <input
              id="articleSearch"
              v-model="search"
              type="text"
              class="form-control"
              placeholder="Name, Artikelnummer ..."
              @keyup.enter="loadArticles"
            />
          </div>
          <div class="col-md-2">
            <button class="btn btn-outline-secondary w-100" @click="loadArticles">
              Suchen
            </button>
          </div>
          <div class="col-md-3">
            <button v-if="canWrite" class="btn btn-primary w-100" @click="openCreate">
              Neuer Artikel
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="canImport || canExport" class="card mb-4">
      <div class="card-body">
        <h5 class="card-title">Import / Export</h5>
        <MasterDataImportPanel v-if="canImport" kind="article" label="Artikel" :allow-csv="true" @imported="loadArticles" />
        <div v-if="canExport" class="d-flex gap-2"><select id="articleExportFormat" v-model="exportFormat" class="form-select w-auto" aria-label="Exportformat"><option value="json">JSON</option><option value="csv">CSV</option></select><button class="btn btn-outline-success" @click="exportArticles">Exportieren</button></div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div class="table-responsive">
          <table class="table table-striped table-hover">
            <thead>
              <tr>
                <th>Artikelnr.</th>
                <th>Name</th>
                <th>Einheit</th>
                <th class="text-end">Netto</th>
                <th class="text-end">Steuer %</th>
                <th v-if="hasActions" class="text-end">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="article in articles" :key="article.id">
                <td>{{ article.articleNumber || "-" }}</td>
                <td>{{ article.name || "-" }}</td>
                <td>{{ article.unitCode || "-" }}</td>
                <td class="text-end">{{ formatNumber(article.netUnitPrice) }}</td>
                <td class="text-end">{{ formatNumber(article.taxPercent) }}</td>
                <td v-if="hasActions" class="text-end">
                  <button v-if="canWrite" class="btn btn-sm btn-outline-primary me-2" @click="openEdit(article)">Bearbeiten</button>
                  <button v-if="canCopy" class="btn btn-sm btn-outline-secondary me-2" @click="openCopy(article)">Duplizieren</button>
                  <button v-if="canDelete" class="btn btn-sm btn-outline-danger" @click="deleteArticle(article)">Löschen</button>
                </td>
              </tr>
              <tr v-if="articles.length === 0">
                <td :colspan="hasActions ? 6 : 5" class="text-center text-muted py-4">Keine Artikel gefunden.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="showModal && canWrite" class="modal fade show d-block" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isEdit ? "Artikel bearbeiten" : isCopy ? "Artikel duplizieren" : "Neuer Artikel" }}</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <p v-if="isCopy" class="alert alert-info">Artikelnummer und Global ID wurden geleert. Die Kopie wird erst beim Speichern angelegt.</p>
            <div class="row g-3">
              <div class="col-md-4">
                <label class="form-label" for="articleNumber">Artikelnummer</label>
                <input id="articleNumber" v-model="form.articleNumber" type="text" class="form-control" />
              </div>
              <div class="col-md-8">
                <label class="form-label" for="articleName">Name</label>
                <input id="articleName" v-model="form.name" type="text" class="form-control" />
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div class="col-md-3">
                <SearchableDropdown
                  input-id="articleUnitCode"
                  label="UnitCode"
                  :model-value="form.unitCode"
                  @update:modelValue="val => { form.unitCode = val; }"
                  :options="masterDataStore.quantityCodes"
                />
              </div>
              <div class="col-md-3">
                <label class="form-label" for="articleNetUnitPrice">NetUnitPrice</label>
                <input id="articleNetUnitPrice" v-model.number="form.netUnitPrice" type="number" step="0.0001" class="form-control" />
              </div>
              <div class="col-md-3">
                <label class="form-label" for="articleTaxPercent">TaxPercent</label>
                <input id="articleTaxPercent" v-model.number="form.taxPercent" type="number" step="0.01" class="form-control" />
              </div>
              <div class="col-md-3">
                <SearchableDropdown
                  input-id="articleCategoryCode"
                  label="CategoryCode"
                  :model-value="form.categoryCode"
                  @update:modelValue="val => { form.categoryCode = val; }"
                  :options="masterDataStore.taxCategoryCodes"
                />
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div class="col-md-12">
                <label class="form-label" for="articleGlobalId">GlobalID</label>
                <input id="articleGlobalId" v-model="form.globalID" type="text" class="form-control" />
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" type="button" @click="closeModal">Abbrechen</button>
            <button class="btn btn-primary" type="button" :disabled="!canWrite || isSaving" @click="saveArticle">Speichern</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MasterDataImportPanel from "@/components/Administration/MasterDataImportPanel.vue";
import ArticleService from "@/services/ArticleService";
import FileTemplateService from "@/services/FileTemplateService";
import { useUxStore } from "@/stores/uxStore";
import { useMasterDataStore } from "@/stores/masterDataStore";
import { useUserStore } from "@/stores/userStore";
import { copyArticle } from "@/utils/masterDataCopy.cjs";
import SearchableDropdown from "@/components/Shared/SearchableDropdown.vue";

const emptyForm = () => ({
  id: "",
  articleNumber: "",
  name: "",
  unitCode: "",
  globalID: "",
  netUnitPrice: null,
  taxPercent: null,
  categoryCode: "",
});

export default {
  name: "ArticlesAdminView",
  components: { MasterDataImportPanel, SearchableDropdown },
  data() {
    return {
      articles: [],
      search: "",
      showModal: false,
      isEdit: false,
      isCopy: false,
      isSaving: false,
      form: emptyForm(),
      exportFormat: "json",
      uxStore: useUxStore(),
      masterDataStore: useMasterDataStore(),
      userStore: useUserStore(),
    };
  },
  computed: {
    canWrite() { return this.userStore.hasPermission("articles.write"); },
    canCopy() { return this.canWrite && this.userStore.hasPermission("articles.read"); },
    canDelete() { return this.userStore.hasPermission("articles.delete"); },
    canImport() {
      return this.userStore.hasPermission("articles.import");
    },
    canExport() {
      return this.userStore.hasPermission("articles.export") && this.userStore.hasPermission("files.read");
    },
    hasActions() { return this.canWrite || this.canDelete; },
  },
  methods: {
    formatNumber(value) {
      if (value === null || value === undefined) return "-";
      if (typeof value !== "number") return value;
      return value.toLocaleString(undefined, { maximumFractionDigits: 6 });
    },
    async loadArticles() {
      const response = await ArticleService.getAll(this.search || null, 1, 200);
      this.articles = Array.isArray(response?.data) ? response.data : [];
    },
    openCreate() {
      if (!this.canWrite) return;
      this.isEdit = false;
      this.isCopy = false;
      this.form = emptyForm();
      this.showModal = true;
    },
    async openEdit(article) {
      if (!this.canWrite) return;
      this.isEdit = true;
      this.isCopy = false;
      const response = await ArticleService.getById(article.id);
      const full = response?.data || article;
      const base = emptyForm();
      this.form = {
        ...base,
        ...full,
        unitCode: full.unitCode || base.unitCode,
        categoryCode: full.categoryCode || base.categoryCode,
      };
      this.showModal = true;
    },
    async openCopy(article) {
      if (!this.canCopy) return;
      const response = await ArticleService.getById(article.id);
      if (!response?.data || !this.canCopy) return;
      this.isEdit = false;
      this.isCopy = true;
      this.form = { ...emptyForm(), ...copyArticle(response.data) };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    async saveArticle() {
      if (!this.canWrite || this.isSaving) return;
      this.isSaving = true;
      try {
        const payload = {
          articleNumber: this.form.articleNumber || null,
          name: this.form.name || null,
          unitCode: this.form.unitCode || null,
          globalID: this.form.globalID || null,
          netUnitPrice: this.form.netUnitPrice,
          taxPercent: this.form.taxPercent,
          categoryCode: this.form.categoryCode || null,
        };

        if (this.isEdit) {
          await ArticleService.update(this.form.id, payload);
        } else {
          await ArticleService.create(payload);
        }

        this.closeModal();
        await this.loadArticles();
      } finally {
        this.isSaving = false;
      }
    },
    async deleteArticle(article) {
      if (!this.canDelete) return;
      const confirmed = window.confirm(`Artikel "${article.name || article.articleNumber || article.id}" wirklich löschen?`);
      if (!confirmed) return;
      await ArticleService.remove(article.id);
      await this.loadArticles();
    },
    async exportArticles() {
      if (!this.canExport) return;
      const response = await ArticleService.export(this.exportFormat);
      const data = response?.data;
      if (!data?.fileId) {
        return;
      }

      await FileTemplateService.downloadFile(data.fileId, data.fileName || "articles");
    },
  },
  mounted() {
    if (!this.userStore.me) this.userStore.loadMe().catch(() => {});
    this.loadArticles();

    // Keep the admin modules consistent with the invoice module: use masterdata-backed dropdowns.
    this.masterDataStore.fetchQuantityCodes();
    this.masterDataStore.fetchTaxCategoryCodes();
  },
};
</script>
